import type { CSSProperties } from "vue";
import { useSmoothAnimation, type UseSmoothAnimation } from "../useSmoothAnimation";
import { lerp } from "math";
import { useResponsive } from "../useResponsive";

const ANIMATION_DURATION = 0.4;
const ANIMATION_SMOOTHNESS = 1.0;

const MIN_WIDTH = 20;

export type UseDrawer = {
  expanded: ComputedRef<boolean>;
  expandAnimation: UseSmoothAnimation;
  setExpanded: (expanded: boolean) => void;
  toggleExpanded: () => void;
  width: ComputedRef<number>;
  setWidth: (fn: (old: number) => number) => void;
  setDirection: (direction: number) => void;
  toggleDirection: () => void;
  direction: ComputedRef<number>;
  style: ComputedRef<CSSProperties>;
  __v_skip: true;
}

export type UseDrawerProps = {
  animation?: UseSmoothAnimation;
}

export const useDrawer = (props: UseDrawerProps = {}): UseDrawer => {
  const expanded = ref<boolean>(false);
  const width = ref<number>(50);
  const direction = ref<number>(1);

  const responsive = useResponsive();

  

  const setWidth = (fn: (old: number) => number) => {
    width.value = Math.min(Math.max(MIN_WIDTH, fn(width.value)), 100);
  }

  watch(responsive.isPhone, (isPhone) => {
    if (isPhone) {
      setWidth(() => 100);
    }
  }, { immediate: true });

  const expandAnimation = useSmoothAnimation({
    initial: 0.5
  });

  watch(expandAnimation.current, (f) => {
    setWidth(() => f * 100.0);
  });

  const setExpanded = (exp: boolean) => {
    expanded.value = exp;

    if (exp) {
      expandAnimation.to(1.0, { duration: ANIMATION_DURATION, smoothness: ANIMATION_SMOOTHNESS });
    } else {
      expandAnimation.to(0.5, { duration: ANIMATION_DURATION, smoothness: ANIMATION_SMOOTHNESS });
    }
  }

  const toggleExpanded = () => {
    setExpanded(!expanded.value);
  }

  const setDirection = (dir: number) => {
    direction.value = dir;
  }

  const toggleDirection = () => {
    setDirection(-direction.value);
  }


  const opacity = computed(() => {
    const anim = props.animation;
    if (!anim) return 100.0;
    return lerp(0.0, 100.0, anim.current.value);
  }) 

  const style = computed((): CSSProperties => {
    return {
      width: `${width.value}%`,
      opacity: `${opacity.value}%`
    }
  })

  return {
    expanded: computed(() => expanded.value),
    expandAnimation,
    setExpanded,
    toggleExpanded,
    width: computed(() => Math.max(MIN_WIDTH, width.value)),
    setWidth,
    setDirection,
    toggleDirection,
    direction: computed(() => direction.value),
    style,
    __v_skip: true
  }
}
